import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  MemberSearchApi,
  type MemberTxnDetail,
} from "@/api/giftcard.api";
import { MerchantApi } from "@/api/merchant.api";
import { BASE_URL } from "@/config";
import { mapVerifiedMemberDetails } from "../mapVerifiedMember";
import { mapActiveCouponsToItems } from "../mapMemberRewards";
import { benefits } from "../mockData";
import type { CouponItem, LedgerRow, MemberProfile, MemberStat } from "../types";
import { BenefitCard } from "./BenefitCard";
import { CouponCard } from "./CouponCard";
import { LedgerTable } from "./LedgerTable";
import { MemberDashboardSkeleton } from "./MemberDashboardSkeleton";
import { MemberHeader } from "./MemberHeader";
import { ProfileCard } from "./ProfileCard";
import { StatCard } from "./StatCard";
import { PublicOfferDetailModal } from "@/pages/offers/shared/PublicOfferDetailModal";
import type {
  GetProgramDetailsOfferPointResponse,
  PublicOffer,
} from "@/pages/offers/shared/publicOffer.types";
import {
  mapProgramOfferToPublicOffer,
  resolvePublicAssetUrl,
} from "@/pages/offers/shared/publicOffer.utils";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { MemberOfferCard } from "./MemberOfferCard";
import { Tag } from "lucide-react";

/** Static Offer Point program code until dynamic selection is wired */
const OFFER_POINT_PROGRAM_CODE = "OP002";

function getAvatarInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "M";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function formatInvoiceAmt(value: number | string | undefined) {
  if (value === undefined || value === null || value === "") return "0";
  const numeric =
    typeof value === "number"
      ? value
      : Number(String(value).replace(/[^\d.-]/g, ""));
  if (Number.isNaN(numeric)) return "0";
  return numeric.toLocaleString("en-IN");
}

function formatPointsEarned(value: number | string | null | undefined) {
  if (value === undefined || value === null || value === "") return "—";
  const numeric =
    typeof value === "number"
      ? value
      : Number(String(value).replace(/[^\d.-]/g, ""));
  if (Number.isNaN(numeric)) return "—";
  return numeric.toLocaleString("en-IN");
}

function mapTxnDetailsToLedgerRows(txns: MemberTxnDetail[] = []): LedgerRow[] {
  return txns.map((txn, index) => {
    const raw = txn as MemberTxnDetail & {
      PointsEarned?: number | string | null;
    };
    return {
      id: `${txn.invoiceNo ?? "txn"}-${txn.counterId ?? index}`,
      counterId: txn.counterId ?? "—",
      invoiceNo: txn.invoiceNo ?? "—",
      transactionType: txn.transactionType ?? "—",
      invoiceAmt: formatInvoiceAmt(txn.invoiceAmt),
      pointsEarned: formatPointsEarned(
        raw.pointsEarned ?? raw.PointsEarned
      ),
    };
  });
}

function mapTierOffersFromResponse(
  responseData: GetProgramDetailsOfferPointResponse
): PublicOffer[] {
  const apiBaseUrl = BASE_URL;
  const program = responseData.programVO;
  const programName = (program?.programName || "").trim() || "Program";
  const programLogoUrl = resolvePublicAssetUrl(
    apiBaseUrl,
    program?.programLogoUri
  );
  const merchants = responseData.merchantVOList || [];
  const defaultMerchantImageUrl = resolvePublicAssetUrl(
    apiBaseUrl,
    merchants.find((m) => (m.merchantImageUri || "").trim())?.merchantImageUri
  );
  const rawList = Array.isArray(responseData.merchantOffersVOList)
    ? responseData.merchantOffersVOList
    : [];

  return rawList
    .filter((item) => item?.isActive !== false)
    .map((item) =>
      mapProgramOfferToPublicOffer(item, {
        apiBaseUrl,
        programName,
        programLogoUrl,
        defaultMerchantImageUrl,
      })
    );
}

interface MemberDashboardProps {
  initialProfile: MemberProfile;
  initialStats: MemberStat[];
}

export function MemberDashboard({
  initialProfile,
  initialStats,
}: MemberDashboardProps) {
  const [member, setMember] = useState<MemberProfile>(initialProfile);
  const [stats, setStats] = useState<MemberStat[]>(initialStats);
  const [ledgerRows, setLedgerRows] = useState<LedgerRow[]>([]);
  const [activeCoupons, setActiveCoupons] = useState<CouponItem[]>([]);
  const [tierOffers, setTierOffers] = useState<PublicOffer[]>([]);
  const [selectedOffer, setSelectedOffer] = useState<PublicOffer | null>(null);
  const [isLoadingTxns, setIsLoadingTxns] = useState(true);
  const [isLoadingMemberDetails, setIsLoadingMemberDetails] = useState(true);
  const [isLoadingTierOffers, setIsLoadingTierOffers] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const hasLoadedOnce = useRef(false);
  const fetchIdRef = useRef(0);

  const loadDashboardData = useCallback(
    async (mode: "initial" | "refresh" = "initial") => {
      const mobileNo = initialProfile.mobile?.replace(/\D/g, "");
      if (!mobileNo) {
        setIsLoadingTxns(false);
        setIsLoadingMemberDetails(false);
        setIsLoadingTierOffers(false);
        setIsRefreshing(false);
        return;
      }

      const fetchId = ++fetchIdRef.current;
      const isStale = () => fetchId !== fetchIdRef.current;

      if (mode === "refresh") {
        setIsRefreshing(true);
        setIsLoadingTxns(true);
        setIsLoadingTierOffers(true);
      } else {
        setIsLoadingMemberDetails(true);
        setIsLoadingTxns(true);
      }

      const loadTransactions = async () => {
        try {
          const data = await MemberSearchApi.getTransactionsByMobileNo({
            p_MobileNo: mobileNo,
          });
          if (isStale()) return;
          setLedgerRows(mapTxnDetailsToLedgerRows(data?.txnDetails));
        } catch (err) {
          if (!isStale()) {
            console.error("GetTransactionsByMobileNo failed:", err);
            setLedgerRows([]);
          }
        } finally {
          if (!isStale()) setIsLoadingTxns(false);
        }
      };

      const loadMemberAndOffers = async () => {
        try {
          const data = await MemberSearchApi.getMemberDetails({
            p_MobileNo: mobileNo,
          });
          if (isStale()) return;

          const detail = data?.newMemberDetails?.[0];
          if (detail) {
            const mapped = mapVerifiedMemberDetails(
              detail,
              mobileNo,
              initialProfile
            );
            setMember(mapped.profile);
            setStats(mapped.stats);
          }

          setActiveCoupons(mapActiveCouponsToItems(data?.activeCoupons));

          const memberTier =
            detail?.tier != null && String(detail.tier).trim()
              ? String(detail.tier).trim()
              : "";

          setIsLoadingTierOffers(true);
          try {
            const tierOfferRes =
              await MerchantApi.getProgramDetailsWithTierOfferPoint({
                programCode: OFFER_POINT_PROGRAM_CODE,
                tier: memberTier,
              });
            if (isStale()) return;

            const responseData = (tierOfferRes?.data ||
              {}) as GetProgramDetailsOfferPointResponse;
            setTierOffers(mapTierOffersFromResponse(responseData));
          } catch (tierErr) {
            if (!isStale()) {
              console.error(
                "getProgramDetailsWithTierOfferPoint failed:",
                tierErr
              );
              setTierOffers([]);
            }
          } finally {
            if (!isStale()) setIsLoadingTierOffers(false);
          }
        } catch (err) {
          if (!isStale()) {
            console.error("GetMemberDetails failed:", err);
          }
        } finally {
          if (!isStale()) {
            setIsLoadingMemberDetails(false);
            hasLoadedOnce.current = true;
          }
        }
      };

      try {
        await Promise.all([loadTransactions(), loadMemberAndOffers()]);
      } finally {
        if (!isStale()) setIsRefreshing(false);
      }
    },
    [initialProfile]
  );

  useEffect(() => {
    void loadDashboardData("initial");
  }, [loadDashboardData]);

  const handleRefresh = () => {
    if (isRefreshing || isLoadingMemberDetails) return;
    void loadDashboardData("refresh");
  };

  if (isLoadingMemberDetails && !hasLoadedOnce.current) {
    return <MemberDashboardSkeleton />;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.35 }}
      className="mx-auto max-w-6xl space-y-8 px-4 py-8 sm:px-6 sm:py-10 lg:px-8 lg:py-12"
    >
      <MemberHeader
        member={member}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map((stat, index) => (
          <StatCard key={stat.id} stat={stat} index={index} />
        ))}
      </div>

      <LedgerTable rows={ledgerRows} isLoading={isLoadingTxns} />

      <section className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm sm:p-6">
        <Accordion type="single" collapsible defaultValue="tier-offers">
          <AccordionItem value="tier-offers" className="border-0">
            <AccordionTrigger className="py-0 hover:no-underline [&[data-state=open]]:pb-4">
              <div className="flex items-center gap-3 text-left">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
                  <Tag className="h-5 w-5" aria-hidden />
                </div>
                <div>
                  <h2 className="text-lg font-semibold tracking-tight text-slate-900">
                    Offers for You
                  </h2>
                  <p className="mt-0.5 text-sm font-normal text-slate-500">
                    {isLoadingTierOffers
                      ? "Loading tier offers…"
                      : tierOffers.length === 0
                        ? "No offers for your tier"
                        : `${tierOffers.length} offer${tierOffers.length === 1 ? "" : "s"} for your tier`}
                  </p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-0 pt-1">
              {isLoadingTierOffers ? (
                <p className="rounded-xl border border-slate-100 bg-slate-50/80 px-4 py-6 text-center text-sm text-slate-500">
                  Loading offers…
                </p>
              ) : tierOffers.length === 0 ? (
                <p className="rounded-xl border border-slate-100 bg-slate-50/80 px-4 py-6 text-center text-sm text-slate-500">
                  No offers available for your tier
                </p>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                  {tierOffers.map((offer) => (
                    <MemberOfferCard
                      key={offer.id}
                      offer={offer}
                      onOpenDetail={setSelectedOffer}
                    />
                  ))}
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </section>

      <PublicOfferDetailModal
        offer={selectedOffer}
        onOpenChange={(open) => {
          if (!open) setSelectedOffer(null);
        }}
      />

      <section className="space-y-6 rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm sm:p-6">
        <div>
          <h2 className="text-lg font-semibold tracking-tight text-slate-900">
            My Rewards
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Redeem points and exclusive member perks
          </p>
        </div>

        <div className="rounded-2xl border border-amber-100 bg-gradient-to-br from-amber-50/70 via-white to-slate-50 p-5 sm:p-6">
          <h3 className="text-xs font-bold uppercase tracking-[0.14em] text-amber-600 sm:text-sm">
            Member Benefits
          </h3>
          <ul className="mt-4 space-y-3.5">
            {benefits.map((benefit) => (
              <BenefitCard key={benefit.id} benefit={benefit} />
            ))}
          </ul>
        </div>
      </section>

      <section className="space-y-5">
        <div>
          <h2 className="text-lg font-semibold tracking-tight text-slate-900">
            Active Coupons
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Ready-to-use offers on your account
          </p>
        </div>
        {activeCoupons.length === 0 ? (
          <p className="rounded-2xl border border-slate-100 bg-slate-50/80 px-4 py-8 text-center text-sm text-slate-500">
            No active coupons available
          </p>
        ) : (
          <div className="grid gap-4 lg:grid-cols-1 xl:grid-cols-1">
            {activeCoupons.map((coupon) => (
              <CouponCard key={coupon.id} coupon={coupon} />
            ))}
          </div>
        )}
      </section>

      <ProfileCard
        member={member}
        onSave={(updates) => {
          setMember((prev) => ({
            ...prev,
            ...updates,
            avatarInitials: getAvatarInitials(updates.name || prev.name),
          }));
        }}
      />
    </motion.div>
  );
}
